import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useGallery } from './hooks/useGallery'
import { useAuth } from '../../hooks/useAuth'
import { Portal } from '../../components/Portal'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { memberColors } from '../../styles/tokens'
import { getInitials, cn } from '../../lib/utils'

const AddPhotoModal = ({ onAdd, onClose }) => {
  const [url,     setUrl]     = useState('')
  const [caption, setCaption] = useState('')
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!url.trim()) { setError('Photo URL is required.'); return }
    setSaving(true)
    setError('')
    try {
      await onAdd({ storage_path: url.trim(), caption })
      onClose()
    } catch (err) {
      setError(err?.message ?? 'Could not add photo.')
    } finally { setSaving(false) }
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <form className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-card space-y-4"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-text-primary">Add Photo</h2>
            <button type="button" onClick={onClose} className="text-text-muted p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted mb-1 block">Photo URL</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus required
              placeholder="https://…"
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted mb-1 block">Caption (optional)</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="What's happening here?"
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" className="flex-1" disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Add'}
            </Button>
          </div>
        </form>
      </div>
    </Portal>
  )
}

const LightboxModal = ({ photo, onClose, onDelete, currentUserId }) => (
  <Portal>
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold"
            style={{ backgroundColor: memberColors[photo.profiles?.member_color ?? 'violet'] }}>
            {getInitials(photo.profiles?.display_name ?? '?')}
          </div>
          <div>
            <p className="text-white text-xs font-medium">{photo.profiles?.display_name}</p>
            <p className="text-white/60 text-[10px]">{format(parseISO(photo.created_at), 'MMM d, yyyy')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {photo.profiles?.id === currentUserId && (
            <button onClick={() => { onDelete(photo.id); onClose() }} className="text-rose-400 hover:text-rose-300 p-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              </svg>
            </button>
          )}
          <button onClick={onClose} className="text-white/60 hover:text-white p-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <img src={photo.storage_path} alt={photo.caption ?? ''} className="max-w-full max-h-full object-contain rounded-lg" />
      </div>
      {photo.caption && (
        <p className="text-center text-white/80 text-sm px-4 pb-6 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {photo.caption}
        </p>
      )}
    </div>
  </Portal>
)

export const GalleryPage = () => {
  const { user, isAdmin } = useAuth()
  const { data: photos = [], isLoading, addPhoto, deletePhoto } = useGallery()
  const [showAdd,   setShowAdd]   = useState(false)
  const [lightbox,  setLightbox]  = useState(null)

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  return (
    <div className="px-4 py-5 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Family Gallery</h1>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
          Add Photo
        </button>
      </div>

      {photos.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <div className="text-5xl mb-3">📷</div>
          <p className="text-sm">No photos yet. Add your first family memory!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {photos.map((photo) => (
            <button key={photo.id} onClick={() => setLightbox(photo)}
              className="aspect-square rounded-xl overflow-hidden bg-background border border-border hover:border-primary/40 transition-colors">
              <img src={photo.storage_path} alt={photo.caption ?? ''}
                className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {showAdd && (
        <AddPhotoModal onAdd={(f) => addPhoto.mutateAsync(f)} onClose={() => setShowAdd(false)} />
      )}
      {lightbox && (
        <LightboxModal
          photo={lightbox}
          currentUserId={user?.id}
          onDelete={(id) => deletePhoto.mutate(id)}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}
